<template>
  <div class="page-container">
    <div class="achievement-market-page" :class="{ 'locale-zh-CN': isZhLocale }">
      <!-- 页面头部 -->
      <PageHeader :config="config" @module-click="handleModuleClick" />

      <!-- 面包屑导航 -->
      <Breadcrumb :items="breadcrumbItems" />

      <!-- 头部Banner -->
      <SearchBanner
        :title-main1="marketConfig.title1 || ''"
        :title-main2="marketConfig.title2 || ''"
        :subtitle="marketConfig.subtitle"
        :placeholder="marketConfig.searchPlaceholder"
        :button-text="marketConfig.searchButton || ''"
        :keyword.sync="searchKeyword"
        :background-image="localeImg('banner/gongxuzhongxin-chengguochaoshi.png')"
        search-object="achievement"
        @search="handleSearch"
      />

      <!-- 筛选条件区 -->
      <div class="filter-section">
        <!-- 成果类别（一级） -->
        <div class="filter-row">
          <div class="filter-label">{{ marketConfig.filters?.categoryLabel || '' }}</div>
          <div class="filter-options">
            <div
              v-for="item in categoryMainOptions"
              :key="item.value"
              :class="['filter-option', { active: activeCategoryMain === item.value }]"
              @click="handleCategoryMainChange(item.value)"
            >
              {{ item.label }}
            </div>
          </div>
        </div>

        <!-- 成果子类（二级） -->
        <div class="filter-row" v-if="visibleCategorySubOptions.length > 0">
          <div class="filter-label">{{ marketConfig.filters?.subCategoryLabel || '成果子类:' }}</div>
          <div class="filter-options">
            <div
              v-for="item in visibleCategorySubOptions"
              :key="item.value"
              :class="['filter-option', { active: activeCategorySub === item.value }]"
              @click="activeCategorySub = item.value; handleFilterChange()"
            >
              {{ item.label }}
            </div>
          </div>
        </div>

        <!-- 所属领域 -->
        <div class="filter-row">
          <div class="filter-label">{{ marketConfig.filters?.fieldLabel || '' }}</div>
          <div class="filter-options">
            <div
              v-for="item in fieldOptions"
              :key="item.value"
              :class="['filter-option', { active: activeField === item.value }]"
              @click="activeField = item.value; handleFilterChange()"
            >
              {{ item.label }}
            </div>
          </div>
        </div>

        <!-- 技术成熟度 -->
        <div class="filter-row" v-if="false">
          <div class="filter-label">{{ marketConfig.filters?.maturityLabel || '' }}</div>
          <div class="filter-options">
            <div
              v-for="item in maturityOptions"
              :key="item.value"
              :class="['filter-option', { active: activeMaturity === item.value }]"
              @click="activeMaturity = item.value; handleFilterChange()"
            >
              {{ item.label }}
            </div>
          </div>
        </div>

        <!-- 转化方式 -->
        <div class="filter-row" v-if="false">
          <div class="filter-label">{{ marketConfig.filters?.transferLabel || '' }}</div>
          <div class="filter-options">
            <div
              v-for="item in transferOptions"
              :key="item.value"
              :class="['filter-option', { active: activeTransfer === item.value }]"
              @click="activeTransfer = item.value; handleFilterChange()"
            >
              {{ item.label }}
            </div>
          </div>
        </div>
      </div>

      <!-- 成果列表 -->
      <div class="achievement-list-container">
        <div v-if="loading" class="loading-container">
          <div class="loading-text">{{ paginationConfig?.loading || '' }}</div>
        </div>
        <AchievementList 
          v-else
          :items="achievementList"
          @item-click="handleItemClick"
        />

        <!-- 分页 -->
        <div class="pagination">
          <span class="pagination-info">{{ pageInfoText }}</span>
          <div class="pagination-controls">
            <button class="page-btn" :disabled="currentPage === 1" @click="goFirstPage">{{ paginationConfig?.firstPage || '' }}</button>
            <button class="page-btn" :disabled="currentPage === 1" @click="prevPage">{{ paginationConfig?.prevPage || '' }}</button>
            <button
              v-for="page in visiblePages"
              :key="page"
              :class="['page-number', { active: currentPage === page }]"
              @click="handlePageChange(page)"
            >
              {{ page }}
            </button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="nextPage">{{ paginationConfig?.nextPage || '' }}</button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="goLastPage">{{ paginationConfig?.lastPage || '' }}</button>
          </div>
        </div>
      </div>

      <!-- 页脚 -->
      <PageFooter />
    </div>
  </div>
</template>

<script>
import { applyPageScale, setupPageScaleObserver, teardownPageScaleObserver } from '@/utils/pageScale'
import { localeImg } from '@/utils/localeAsset'
import { getSupplyDemandConfig } from '@/config/supplyDemandConfig'
import PageHeader from '@/components/PageHeader.vue'
import PageFooter from '@/components/PageFooter.vue'
import SearchBanner from '@/components/SearchBanner.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import AchievementList from '@/components/AchievementList.vue'
import supplyDemandApi from '@/api/supplyDemand'
import { getCurrentLocale } from '@/utils/i18nText'
import { buildSubCategoryTabsFromDict, getVisibleSubCategoryTabs } from '@/utils/filterTabs'

export default {
  name: 'AchievementMarket',
  components: {
    PageFooter,
    PageHeader,
    SearchBanner,
    Breadcrumb,
    AchievementList
  },
  data() {
    return {
      config: {},
      searchKeyword: '',
      activeCategoryMain: 'all',
      activeCategorySub: '',
      activeField: 'all',
      activeMaturity: 'all',
      activeTransfer: 'all',
      currentPage: 1,
      pageSize: 12,
      totalCount: 0,

      // 面包屑数据
      breadcrumbItems: [],

      // 筛选选项
      categoryDictData: [],
      categoryMainOptions: [],
      categorySubOptions: [],
      fieldOptions: [],
      maturityOptions: [],
      transferOptions: [],

      // 成果列表
      achievementList: [],
      loading: false
    }
  },
  computed: {
    marketConfig() {
      return this.config.achievementMarket || {}
    },
    paginationConfig() {
      return this.marketConfig.pagination || {}
    },
    totalPages() {
      const pages = Math.ceil(this.totalCount / this.pageSize)
      return pages > 0 ? pages : 1
    },
    // 页码最多显示 6 个，采用滑动窗口
    visiblePages() {
      const total = this.totalPages
      const current = this.currentPage
      const max = 6
      if (total <= max) {
        return Array.from({ length: total }, (_, i) => i + 1)
      }
      let start = Math.max(1, current - 2)
      let end = start + max - 1
      if (end > total) {
        end = total
        start = end - max + 1
      }
      const pages = []
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    },
    pageInfoText() {
      const tpl = this.paginationConfig?.pageInfo || ''
      return tpl.replace('{total}', this.totalCount).replace('{pageSize}', this.pageSize)
    },
    visibleCategorySubOptions() {
      return getVisibleSubCategoryTabs(this.categorySubOptions)
    }
  },
  created() {
    this.loadConfig()
  },
  mounted() {
    this.$nextTick(() => {
      this.setScale()
      this.observePageResize()
    })
    window.addEventListener('resize', this.setScale)
  },
  beforeDestroy() {
    teardownPageScaleObserver(this)
    window.removeEventListener('resize', this.setScale)
  },
  methods: {
    // 设置缩放比例
    setScale() {
      applyPageScale(this.getPageScaleEl())
    },

    getPageScaleEl() {
      return this.$el.querySelector('.achievement-market-page')
    },

    observePageResize() {
      setupPageScaleObserver(this, () => this.getPageScaleEl())
    },

    // 加载配置
    loadConfig() {
      this.config = getSupplyDemandConfig(getCurrentLocale())
      const breadcrumb = this.marketConfig.breadcrumb || {}
      this.breadcrumbItems = [
        { label: breadcrumb.home || '', path: '/index' },
        { label: breadcrumb.center || '', path: '/supply-demand' },
        { label: breadcrumb.current || '', path: '' }
      ]
      const kw = this.$route && this.$route.query && this.$route.query.keyword
      if (kw) this.searchKeyword = String(kw)

      // 加载字典数据
      this.loadDictData()
      // 加载成果列表
      this.loadAchievementList()
    },

    // 解析字典 rows
    normalizeDictRows(data) {
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.rows)) return data.rows
      if (data && typeof data === 'object') return Object.values(data).flat()
      return []
    },

    // 构建成果类别（一级 / 二级）
    buildCategoryOptions(dictData) {
      const categoryDict = (dictData || []).filter(item => item && item.dictCode === 'yAchievementTypev2')
      this.categoryDictData = categoryDict

      const mainTypes = categoryDict
        .filter(item => item.parentCode === null || item.parentCode === '' || item.parentCode === undefined)
        .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))

      this.categoryMainOptions = [
        { label: '全部', value: 'all' },
        ...mainTypes.map(item => ({
          label: item.itemValue || '',
          value: item.itemCode || ''
        }))
      ]

      this.updateCategorySubOptions(this.activeCategoryMain)
    },

    updateCategorySubOptions(mainValue) {
      if (!mainValue || mainValue === 'all') {
        this.categorySubOptions = []
        this.activeCategorySub = ''
        return
      }

      const subTypes = this.categoryDictData.filter(
        item => String(item.parentCode || '') === String(mainValue)
      )
      this.categorySubOptions = buildSubCategoryTabsFromDict(subTypes, { allLabel: '全部', allValue: '' })
      this.activeCategorySub = this.categorySubOptions[0]?.value ?? ''
    },

    handleCategoryMainChange(value) {
      this.activeCategoryMain = value
      this.updateCategorySubOptions(value)
      this.handleFilterChange()
    },

    getCategoryQueryParams() {
      const params = {}
      if (this.activeCategoryMain && this.activeCategoryMain !== 'all') {
        params.firstCategory = this.activeCategoryMain
      }
      if (this.activeCategorySub) {
        params.secondCategory = this.activeCategorySub
      }
      return params
    },

    // 加载字典数据
    async loadDictData() {
      try {
        const dictCodeList = ['yAchievementTypev2', 'yField', 'yTechMaturity', 'yTransformMethod']
        const response = await supplyDemandApi.getDictData(dictCodeList)
        
        const result = response.data || {}
        if (result.status === 0) {
          const dictData = this.normalizeDictRows(result.data)
          
          this.buildCategoryOptions(dictData)
          // 处理所属领域
          const fieldDict = dictData.filter(item => item && item.dictCode === 'yField')
          this.fieldOptions = [
            { label: '全部', value: 'all' },
            ...fieldDict.map(item => ({
              label: item.itemValue,
              value: item.itemCode
            }))
          ]

          // 处理技术成熟度
          const maturityDict = dictData
            .filter(item => item && item.dictCode === 'yTechMaturity')
            .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))
          this.maturityOptions = [
            { label: '全部', value: 'all' },
            ...maturityDict.map(item => ({
              label: item.itemValue,
              value: item.itemCode
            }))
          ]

          // 处理转化方式
          const transferDict = dictData
            .filter(item => item && item.dictCode === 'yTransformMethod')
            .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))
          this.transferOptions = [
            { label: '全部', value: 'all' },
            ...transferDict.map(item => ({
              label: item.itemValue,
              value: item.itemCode
            }))
          ]
        } else {
          this.categoryMainOptions = this.marketConfig.categoryOptions || []
          this.categorySubOptions = []
          this.fieldOptions = this.marketConfig.fieldOptions || []
          this.maturityOptions = this.marketConfig.maturityOptions || []
          this.transferOptions = this.marketConfig.transferOptions || []
        }
      } catch (error) {
        console.error('加载字典数据失败:', error)
        this.categoryMainOptions = this.marketConfig.categoryOptions || []
        this.categorySubOptions = []
        this.fieldOptions = this.marketConfig.fieldOptions || []
        this.maturityOptions = this.marketConfig.maturityOptions || []
        this.transferOptions = this.marketConfig.transferOptions || []
      }
      
      this.activeCategoryMain = this.categoryMainOptions[0]?.value || 'all'
      this.activeCategorySub = ''
      this.activeField = this.fieldOptions[0]?.value || ''
      this.activeMaturity = this.maturityOptions[0]?.value || ''
      this.activeTransfer = this.transferOptions[0]?.value || ''
    },

    // 加载成果列表
    async loadAchievementList() {
      this.loading = true
      try {
        const params = {
          ...this.getCategoryQueryParams(),
          field: this.activeField === 'all' ? '' : this.activeField,
          techMaturity: this.activeMaturity === 'all' ? '' : this.activeMaturity,
          transformMethod: this.activeTransfer === 'all' ? '' : this.activeTransfer,
          // 模糊搜索：从 SearchBanner 的搜索框拿到的关键字
          achievementName: String(this.searchKeyword || '').trim(),
          // 必填：页码/每页条数（统一用 pageNum/pageSize）
          pageNum: this.currentPage,
          pageSize: this.pageSize
        }
        
        const response = await supplyDemandApi.getAchievementSupermarketList(params)
        
        // response.data 是 axios 返回的数据
        const result = response.data || {}
        if (result.status === 0) {
          const data = result.data || {}
          const list = Array.isArray(data.rows) ? data.rows : (Array.isArray(data) ? data : [])
          
          // 转换数据格式
          this.achievementList = list.map(item => {
            // 获取图片URL
            let imageUrl = localeImg('default.png') // 默认图片
            if (item.fileList && item.fileList.length > 0) {
              const firstImage = item.fileList[0]
              if (firstImage.fileId) {
                imageUrl = supplyDemandApi.getImageUrl(firstImage.fileId)
              }
            }
            
            // 处理费用显示
            let priceText = '双方协商'
            const feeType = String(item.transferFeeType ?? '')
            if (feeType === '1') {
              priceText = item.transferFeeTypeLabel || ''
            } else if (item.transferFeeMin != null && item.transferFeeMax != null) {
              priceText = `${item.transferFeeMin}-${item.transferFeeMax}万元`
            } else if (item.transferFeeTypeLabel) {
              priceText = item.transferFeeTypeLabel
            }
            
            return {
              id: item.id,
              title: item.achievementName || '',
              transferType: item.transformMethodLabel || '',
              price: priceText,
              organization: item.unitPublic ? (item.unitName || '') : '',
              phone: item.phonePublic ? (item.contactPhone || '') : '',
              image: imageUrl
            }
          })
          
          // 更新总数
          this.totalCount = data.totalCount || data.total || list.length || 0
        }
      } catch (error) {
        console.error('加载成果列表失败:', error)
        this.$message.error('加载成果列表失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.currentPage = 1
      this.loadAchievementList()
    },

    // 筛选条件变化时重新加载
    handleFilterChange() {
      this.currentPage = 1
      this.loadAchievementList()
    },

    handleModuleClick(module) {
      console.log('点击模块:', module.name)
      if (module.id === 'home') {
        this.$router.push('/index')
      }
    },

    handleItemClick(item) {
      console.log('点击成果:', item.title)
      this.$router.push({
        path: '/supply-demand/detail',
        query: {
          id: item.id,
          type: 'achievement'
        }
      })
    },

    handlePageChange(page) {
      this.currentPage = page
      this.loadAchievementList()
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.loadAchievementList()
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.loadAchievementList()
      }
    },

    goFirstPage() {
      if (this.currentPage === 1) return
      this.currentPage = 1
      this.loadAchievementList()
    },

    goLastPage() {
      if (this.currentPage === this.totalPages) return
      this.currentPage = this.totalPages
      this.loadAchievementList()
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.achievement-market-page {
  width: 1920px;
  min-height: 100vh;
  background: #fff;
  transform-origin: top left;
}

// 筛选条件区
.filter-section {
  width: 1700px;
  margin: 0 auto 30px;
  margin-left: 110px;
  background: #ffffff;
  padding: 24px 30px;
  border-radius: 4px;

  .filter-row {
    display: flex;
    align-items: flex-start;
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    .filter-label {
      min-width: 100px;
      font-family: Source Han Sans CN;
      font-weight: 400;
      font-size: 16px;
      color: #8A7929;
      line-height: 32px;
      flex-shrink: 0;
    }

    .filter-options {
      flex: 1;
      display: flex;
      flex-wrap: wrap;
      gap: 12px;

      .filter-option {
        padding: 6px 16px;
        background: #f8f8f8;
        border-radius: 2px;
        font-family: Source Han Sans CN;
        font-weight: 400;
        font-size: 16px;
        color: #444444;
        line-height: 20px;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
          background: #fff7e6;
          color: #8A7929;
        }

        &.active {
          background: #a0903c;
          color: #ffffff;
        }
      }
    }
  }
}

// 成果列表容器
.achievement-list-container {
  width: 1700px;
  margin: 0 auto 40px;
  margin-left: 110px;

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    padding: 40px;

    .loading-text {
      font-family: Source Han Sans CN;
      font-weight: 400;
      font-size: 16px;
      color: #666666;
    }
  }
}

// 分页
.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0;
  margin-top: 30px;

  .pagination-info {
    font-family: Source Han Sans CN;
    font-size: 13px;
    color: #666666;
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;

    .page-btn,
    .page-number {
      min-width: 36px;
      height: 32px;
      padding: 0 12px;
      background: #ffffff;
      border: 1px solid #dddddd;
      border-radius: 2px;
      font-family: Source Han Sans CN;
      font-size: 13px;
      color: #666666;
      cursor: pointer;
      transition: all 0.3s;

      &:hover:not(:disabled):not(.active) {
        border-color: #a0903c;
        color: #a0903c;
      }

      &.active {
        background: #a0903c;
        border-color: #a0903c;
        color: #ffffff;
      }

      &:disabled {
        background: #f5f5f5;
        border-color: #dddddd;
        color: #cccccc;
        cursor: not-allowed;
      }
    }
  }
}


@import '@/assets/scss/mixins/portal-filter-locale-zh-cn.scss';

.achievement-market-page.locale-zh-CN {
  @include portal-filter-locale-zh-cn;
}
</style>
